import React from 'react';
import filter from 'lodash/filter';
import SectionRichText from './section-rich-text';
import SectionContentGrid from './section-content-grid';
import SectionOffices from './section-offices';
import SectionTestimonial from './section-testimonial';
import { VideoCarousel } from '../video-carousel';
import FaqSection from '../faq-section';
import AwardCarousel from '../award-carousel';
import { AssessmentCaseStudies } from '../assessment-case-studies';
import RelatedInsights from '../related-insights';
import ImageWithTitleset from '../about-us/image-with-titleset';
import ImageWithCaptionTemplate from '../image-with-caption';
import SectionVideo from './section-video';
import Contact from '../contact';
import { Paper, colors, Container } from '../../ui-components';

/**
 * Mapping of the statically created "Section Content Static Component" instances
 * in contentful to their appropriate components. Each property name is a direct
 * reflection to each of the "Names" of these content instances in Contentful
 */

const STATIC_COMPONENT_MAPPING = {
  OFFICES: <SectionOffices/>
};

/**
 * Component which renders sections within a page and assigns the correct
 * "Paper" assignments based on their position in the list of sections
 *
 * @component SectionLayout
 * 
 * @param {object} props
 * 
 * @prop {array} sections
 * @prop {string} geoSpace
 * @prop {string} slug
 * 
 * @returns {JSX}
 */
const SectionLayout = props => {
  const { sections, geoSpace, slug } = props;

  const getSectionComponent = (section, isPaper, isAbovePaper) => {
    const sectionColor = isPaper ? colors?.sandBeige : section?.defaultColor;
    switch (section?.type) {
      case 'CONTENT_COMPONENT': {
        const componentRef = section?.component.toUpperCase();
        return React.cloneElement(STATIC_COMPONENT_MAPPING[componentRef]);
      }
      case 'CONTENT_GRID': {
        let imagesOnly;

        if (section.gridItems) {
          const onlyImages = filter(section.gridItems, { title: null, subTitle: null, url: null });
          imagesOnly = onlyImages.length === section.gridItems.length;
        }

        // Use the CTA component if it's a CTA
        if (section?.ctaLinkLabel && section?.ctaLinkUrl && section?.gridItems === null) {
          return (
            <Contact headline={section.headline} ctaLinkLabel={section.ctaLinkLabel} ctaLinkUrl={section.ctaLinkUrl} />
          );
        } else {
          return (
            <SectionContentGrid
              imageOnly={imagesOnly}
              backgroundColor={sectionColor}
              headline={section.headline}
              subHeadline={section.subHeadline}
              headlineText={section.headlineText}
              gridItems={section.gridItems}
              ctaLinkLabel={section.ctaLinkLabel}
              ctaLinkUrl={section.ctaLinkUrl}
              removeBottomPadding={!isPaper && isAbovePaper}
            />
          );
        }
      }
      case 'CONTENT_VIDEO_CAROUSEL': {
        return (
          <VideoCarousel 
            sectionTitle={section.sectionTitle}
            subtitle={section.title}
            carouselVideos={section.videos} />
        );
      }
      case 'CONTENT_VIDEO': {
        return (
          <>
            <SectionVideo embedUrl={section?.embedUrl} title={section?.title} subTitle={section?.videoDescription} />
          </>
        );
      }
      case 'CONTENT_FAQ_LIST': {
        return (
          <FaqSection faqs={section.questions} />
          // Placeholder for FAQ Quote section
        );
      }
      case 'AWARDS_SECTION': {
        return (
          <Container sectionContainer>
            <AwardCarousel awards={section}></AwardCarousel>
          </Container>
        );
      }
      case 'CONTENT_CASE_STUDIES': {
        return (
          <AssessmentCaseStudies
            caseStudiesSectionTitle={section?.title}
            featuredCaseStudies={section?.featuredCaseStudies}
          />
        );
      }
      case 'CONTENT_FLOATING_IMAGE_TEXT': {
        const contentBlock = {
          label: section?.label,
          title: section?.title,
          bodyRichJSON: section?.text,
          image: section?.image
        };
        return (
          <ImageWithTitleset
            contentBlock={contentBlock}
            imageLeft={!section?.imageAlignment}
            centerTextSection={section?.imageCentered}
            altMobileOrder={section.imageAlignment}
            objectFit={'contain'}
          />
        );
      }
      case 'CONTENT_IMAGE_WITH_CAPTION': {
        return (
          <Container sectionContainer>
            <ImageWithCaptionTemplate
              image={section.image.url}
              alt={section?.image?.caption}
              caption={section?.caption}
              alignment={section?.captionAlignment}
              linkText={section?.linkText}
              linkUrl={section?.linkUrl}
            />
          </Container>
        );
      }
      case 'CONTENT_TESTIMONIAL':
        return (
          <SectionTestimonial
            title={section.title}
            sectionTitle={section.sectionTitle}
            subTitle={section.subTitle}
            linkUrl={section.linkUrl}
            linkLabel={section.linkLabel}
            testimonials={section.testimonials}
          />
        );
      case 'CONTENT_RICH':
        return <SectionRichText sectionData={section} geoSpace={geoSpace} slug={slug} />;
      case 'CONTENT_RELATED_INSIGHTS': {
        const relatedInsights = section?.relatedInsights;
        return (
          <RelatedInsights insights={relatedInsights} />
        );
      }
      default:
        break;
    }
  };

  const hasValidPaperPredecessor = index => {
    return sections[index - 1].defaultColor === colors.pureWhite;
  };

  const hasValidPaperSuccessor = index => {
    return index + 1 < sections.length && sections[index + 1].defaultColor === colors.pureWhite;
  };

  const initSectionGroups = () => {
    let whiteBackground = true;
    let sectionGroups = [];
    let index = 0;

    while (index < sections.length) {
      if (sections[index]?.defaultColor !== colors.pureWhite) {
        let sectionToRender = getSectionComponent(sections[index], false, false);

        sectionGroups.push(sectionToRender);
        whiteBackground = true;
        index++;
      } else if (whiteBackground === true || sections[index]?.type === 'CONTENT_RICH') {
        const paperTearBelow = sections[index + 1] ? sections[index + 1]?.defaultColor === colors.pureWhite : true;
        let sectionToRender = getSectionComponent(sections[index], false, paperTearBelow);

        sectionGroups.push(sectionToRender);
        whiteBackground = false;
        index++;
      } else {
        const nextIndex = index + 1;
        const paperTop = hasValidPaperPredecessor(index);
        const paperBottom = hasValidPaperSuccessor(nextIndex);

        const firstSectionToRender = getSectionComponent(sections[index], true, false);
        const secondSectionToRender =
          nextIndex < sections.length ? getSectionComponent(sections[nextIndex], true, false) : null;

        sectionGroups.push(<Paper top={paperTop}>{firstSectionToRender}</Paper>);
        sectionGroups.push(<Paper bottom={paperBottom}>{secondSectionToRender}</Paper>);

        whiteBackground = true;
        index += 2;
      }
    }

    return sectionGroups;
  };

  return initSectionGroups();
};

SectionLayout.propTypes = {};

export default SectionLayout;

